import {Badge, Link, majorScale, minorScale, Pane} from 'evergreen-ui';
import {observer} from 'mobx-react-lite';
import moment from "moment";

import {useStore} from './useStore';
import {useEffect, useState} from 'react';
import {AgGridColumn, AgGridReact} from "ag-grid-react";
import 'ag-grid-community/dist/styles/ag-grid.min.css';
import 'ag-grid-community/dist/styles/ag-theme-balham.min.css';
import './custom-ag-grid.css';

const OrdersTable = observer(() => {
  const [gridApi, setGridApi] = useState(null);
  const {orderStore} = useStore();

  useEffect(() => {
    if (!gridApi) {
      return;
    }
    if (orderStore.state === 'pending') {
      gridApi.showLoadingOverlay();
    } else {
      gridApi.hideOverlay();
    }
  }, [orderStore.state, gridApi]);

  const defaultColDef = {
    // set every column width
    width: 120,
    // make every column editable
    // editable: true,
    // make every column use 'text' filter by default
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    suppressMenu: true,
    sortable: true,
    unSortIcon: true,
    resizable: true
  };

  const dateFormatter = (params) => {
    return moment(params.value, 'YYYY-MM-DD').format('DD MMM YYYY');
  };

  const dateFilterParams = {
    comparator: (searchDate, cellValue) => {
      if (cellValue == null) {
        return 0;
      }
      const cellMoment = moment(cellValue, 'YYYY-MM-DD');
      const searchMoment = moment(searchDate);

      if (cellMoment.isBefore(searchMoment)) {
        return -1;
      } else if (cellMoment.isAfter(searchMoment)) {
        return 1;
      }
      return 0;
    }
  };

  const TrackingUrlRenderer = (params) => {
    return params.value ?
      <Link href={params.value} target="_blank">{params.data.trackingNumber}</Link> :
      params.data.trackingNumber || ''
  };

  const TagsRenderer = (params) => {
    return (
      <div className="tag-container">
        {params.data.slaDays < 0 && <Badge color="red" marginRight={minorScale(1)}>overdue</Badge>}
        {params.data.isInBatch && <Badge color="neutral" marginRight={minorScale(1)}>in batch</Badge>}
        {params.data.logisticsProvider && <Badge color="green" marginRight={minorScale(1)}>Sent to {params.data.logisticsProvider}</Badge>}
      </div>
    );
  };

  const LoadingOverlay = () => {
    return (
      <span className="ag-overlay-loading-center">Please wait while data are loading</span>
    );
  };

  const onSelectionChanged = () => {
    orderStore.selectedOrders = gridApi.getSelectedRows();
  };

  const onFilterChanged = (event) => {
    orderStore.displayedRowCount = gridApi.getDisplayedRowCount();
  };

  const onGridReady = (params) => {
    setGridApi(params.api);
    orderStore.selectedOrders = [];
  };

  return (
    <Pane flexBasis="100%" height='50vh' marginTop={majorScale(1)} className="ag-theme-balham">
      <AgGridReact
        reactUi={true}
        rowHeight={50}
        defaultColDef={defaultColDef}
        rowData={orderStore.orders}
        onGridReady={onGridReady}
        suppressRowClickSelection={true}
        enableCellTextSelection={true}
        rowSelection={'multiple'}
        onSelectionChanged={onSelectionChanged}
        onFilterChanged={onFilterChanged}
        frameworkComponents={{
          trackingUrlRenderer: TrackingUrlRenderer,
          tagsRenderer: TagsRenderer,
          loadingOverlay: LoadingOverlay
        }}
        loadingOverlayComponent={'loadingOverlay'}>
        <AgGridColumn field="reference"
                      headerName="SO ref."
                      width={160}
                      headerCheckboxSelection={true}
                      headerCheckboxSelectionFilteredOnly={true}
                      checkboxSelection={true}/>
        <AgGridColumn field="salesOrderStatus"
                      headerName="SO status"
                      width={180}/>
        <AgGridColumn field="branchId"
                      headerName="Branch ID"
                      width={110}
                      filter="agNumberColumnFilter"/>
        <AgGridColumn field="estimatedDeliveryDate"
                      headerName="Due date"
                      width={110}
                      valueFormatter={dateFormatter}
                      filter="agDateColumnFilter"
                      filterParams={dateFilterParams}/>
        <AgGridColumn field="slaDays"
                      headerName="SLA days"
                      width={100}
                      filter="agNumberColumnFilter"/>
        <AgGridColumn field="createdDate"
                      headerName="Created at"
                      width={110}
                      valueFormatter={dateFormatter}
                      filter="agDateColumnFilter"
                      filterParams={dateFilterParams}/>
        <AgGridColumn field="deliveryCountry"
                      headerName="Delivery country"
                      width={150}/>
        <AgGridColumn field="carrier"
                      headerName="Carrier"
                      width={90}/>
        <AgGridColumn field="trackingUrl"
                      headerName="Tracking #"
                      width={200}
                      cellRenderer="trackingUrlRenderer"/>
        <AgGridColumn field="tags"
                      headerName="Tags"
                      width={180}
                      cellRenderer="tagsRenderer"/>
      </AgGridReact>
    </Pane>
  );
});

export default OrdersTable;